import React, { useMemo } from 'react';
import { ProductSubcategory } from 'api/types/product';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import CardList from '../CardList';
import { subcategories } from '../SelectSubcategory/utils';
import { parseSuggestedProductsToCardItems, suggestedProductsData } from './utils';
import { NewProductMode } from '../NewProductStepper';
import { Row, Col } from 'antd';
import SelectSubcategory from 'components/SelectSubcategory';
import './SelectProduct.scss';
import { EPriceType } from '../ProductBaseForm/constants';

interface Props {
  mode?: NewProductMode;
}
const DEFAULT_CANCELLATION_POLICY = 'Must cancel 24 hours in advance for a refund.';

const SelectProduct: React.FC<Props> = ({ mode = 'default' }) => {
  const { control, setValue, watch, unregister } = useFormContext();
  const selectedSubcategory = watch('subcategory');

  const items = useMemo(() => {
    const parseItems = parseSuggestedProductsToCardItems(selectedSubcategory, mode === 'quick');
    if (mode === 'quick') {
      return parseItems;
    }
    const subcategory = subcategories.find(({ id }) => id === ProductSubcategory.SomethingElse);
    return [
      {
        ...subcategory,
        id: 0,
        subheader: selectedSubcategory,
        title: 'Create your own product',
        preview: '',
        dividerText: 'OR USE A TEMPLATE',
        priceType: EPriceType.STATIC,
        price: '0',
      },
      ...parseItems,
    ];
  }, [selectedSubcategory, mode]);

  const handleProduct = (value: number, field: ControllerRenderProps<FieldValues, 'product'>) => {
    const product = !value
      ? { id: 0, price: 0, subcategory: selectedSubcategory, priceType: EPriceType.STATIC }
      : suggestedProductsData.find(({ id }) => value === id);
    unregister('product');

    setValue(
      field.name,
      value === field.value?.id
        ? null
        : { ...product, price: product.price ? Number(product.price).toFixed(2) : '' },
      { shouldValidate: true },
    );
    const currentCancellationPolicy = watch('product.cancellationPolicy');
    if (!currentCancellationPolicy) {
      setValue('product.cancellationPolicy', DEFAULT_CANCELLATION_POLICY, { shouldValidate: true });
    }
  };

  return (
    <Row className="SelectProduct" gutter={[0, 24]}>
      {mode === 'quick' && (
        <Col className="SelectProduct-filter" xs={24}>
          <Controller
            control={control}
            name="subcategory"
            render={({ field: { ref: _ref, ...rest } }) => {
              return (
                <SelectSubcategory
                  label="View"
                  {...rest}
                  getOptions={(options) => [
                    { value: null, label: 'All' },
                    ...options.filter(({ value }) => value !== ProductSubcategory.SomethingElse),
                  ]}
                />
              );
            }}
          />
        </Col>
      )}
      <Col xs={24}>
        <Controller
          control={control}
          name="product"
          render={({ field }) => (
            <CardList
              items={items}
              trimDescription
              selectedId={field.value?.id}
              onChange={(value) => handleProduct(value, field)}
            />
          )}
        />
      </Col>
    </Row>
  );
};

export default SelectProduct;
